<template>
  <div
    v-loading="isLoading"
    class="select-food-items"
  >
    <div class="main-header">
      <div class="main-header__left">
        <!-- 點數類型-->
        <div class="main-header__point-type">
          <div class="title">
            {{ $t('OrderFood.SelectTransactionPointType') }}：
          </div>
          <el-radio
            v-for="(point, index) in pointTypeList"
            :key="index"
            v-model="currentSelectedPointType"
            :label="point.value"
          >
            {{ point.title }}
          </el-radio>
        </div>
        <!-- 用餐方式 -->
        <div class="main-header__dining-mode">
          <div class="dining-mode-select">
            <div class="dining-mode-select__title">
              {{ $t('OrderFoodEntry.DiningModeTitle') }}：
            </div>
            <div class="dining-mode-select__options">
              <el-radio v-model="selectedDiningMode" :label="2">
                {{ $t('OrderFoodEntry.TakeoutNow') }}
              </el-radio>
              <el-radio v-model="selectedDiningMode" :label="1" :disabled="clubhouseTables.length === 0">
                {{ $t('OrderFoodEntry.DineIn') }}
                <el-select
                  v-model="selectedTableNo"
                  class="dining-mode-select__table-select"
                  size="small"
                  clearable
                  filterable
                  :placeholder="$t('OrderFoodEntry.TableNoSelectPlaceholder')"
                  :disabled="selectedDiningMode !== 1 || clubhouseTables.length === 0"
                >
                  <el-option
                    v-for="table in clubhouseTables"
                    :key="table.id || table.tableNo"
                    :label="table.tableNo"
                    :value="table.tableNo"
                  />
                </el-select>
              </el-radio>
            </div>
          </div>
        </div>
      </div>
      <div class="main-header__right">
        <!-- 戶別資訊 -->
        <div v-show="currentSelectedHouseholdInfo.tabletId" v-loading="isLoadingHouseholdInfo" class="main-header__household-info">
          <span>{{ $t('OrderFood.Household') }}：{{ currentSelectedHouseholdInfo.tabletNote }}</span>
          <span>{{ $t('OrderFood.NormalPoints') }}：{{ currentSelectedHouseholdInfo.normalPoint }}</span>
          <span>{{ $t('OrderFood.CashPoints') }}：{{ currentSelectedHouseholdInfo.cashPoint }}</span>
          <div class="main-header__update-household-points" @click="handleUpdateHouseholdPoints">
            <span class="main-header__update-household-points-icon"><i class="icon-refresh"></i></span>
            <span class="main-header__update-household-points-text">{{ $t('OrderFood.UpdateHouseholdPoints') }}</span>
          </div>
        </div>
      </div>
    </div>
    <!-- 餐點品項列表 / 已選擇 -->
    <div class="main-container">
      <div class="item-list-container">
        <div class="container-title">
          {{ $t('OrderFood.FoodItemList') }}
        </div>
        <div class="item-types-wrapper">
          <div v-show="!menuSwiperNavBtnStatus.isBegin" class="item-types-wrapper__navigation-btn left" @click="handleSwiperSlide('prev')">
            <i class="el-icon-arrow-left"></i>
          </div>
          <swiper
            ref="menuSwiper"
            class="swiper item-types-swiper"
            :options="menuSwiperOptions"
          >
            <swiper-slide v-for="menu in menuSwiperList" :key="menu.id" class="slide item-types-slide">
              <span class="item-types-slide__content" :class="{isSelected: menu.isSelected}" @click="clickMenu(menu)">{{ menu.name }}</span>
            </swiper-slide>
          </swiper>
          <div v-show="menuSwiperNavBtnStatus.isNextEnable && !menuSwiperNavBtnStatus.isEnd" class="item-types-wrapper__navigation-btn right" @click="handleSwiperSlide('next')">
            <i class="el-icon-arrow-right"></i>
          </div>
        </div>
        <div class="item-list-wrapper">
          <div
            v-for="item in filteredFoodItemList"
            :key="item.id"
            class="select-food-items__menu-item-list"
          >
            <div class="select-food-items__menu-item" :class="{isClicked: currentClickedFoodItemId === item.id}" @click="clickItem(item)">
              <span>{{ item.name }}: {{ item.prices[currentSelectedPointType -1].unitPrice }}</span>
            </div>
          </div>
        </div>
      </div>
      <div class="selected-item-list-container">
        <div class="container-title">
          {{ $t('OrderFood.SelectedFoodItems') }}
        </div>
        <div class="selected-item-list-wrapper">
          <div class="el-table-container" :class="{ 'is-point-insufficient': isPointsInsufficient }">
            <el-table
              :data="selectedFoodItemList"
              height="100%"
              max-height="100%"
              empty-text=" "
              :row-class-name="getRowClassName"
              border
            >
              <el-table-column
                prop="name"
                :label="$t('OrderFood.FoodItem')"
              />
              <el-table-column
                prop="quantity"
                :label="$t('OrderFood.Quantity')"
              >
                <template slot-scope="scope">
                  <el-input-number
                    v-model="scope.row.quantity"
                    :min="1"
                    size="small"
                  />
                </template>
              </el-table-column>
              <el-table-column
                :label="$t('OrderFood.Point')"
                width="80"
              >
                <template slot-scope="scope">
                  {{ scope.row.prices[currentSelectedPointType - 1].unitPrice }}
                </template>
              </el-table-column>
              <el-table-column>
                <template slot="header">
                  <div class="point-container">
                    <span class="point-container__title">{{ $t('OrderFood.Spec') }}</span>
                    <el-tooltip class="item" effect="dark" :content="$t('OrderFood.SpecManagementCreateTooltip')" placement="top">
                      <span class="point-container__icon"><i class="el-icon-question"></i></span>
                    </el-tooltip>
                  </div>
                </template>
                <template slot-scope="scope">
                  <div v-if="scope.row.hasSpec">
                    <div>{{ scope.row.displaySpec }}</div>
                    <div>
                      <el-button type="primary" size="mini" @click="handleOpenSelectSpecDialog(scope.$index)">
                        {{ !scope.row.displaySpec ? $t('OrderFood.PleaseSelect') : $t('OrderFood.EditSpec') }}
                      </el-button>
                    </div>
                  </div>
                  <div v-else>
                    {{ $t('OrderFood.NoSpec') }}
                  </div>
                </template>
              </el-table-column>
              <el-table-column
                :label="$t('OrderFood.Subtotal')"
                width="80"
              >
                <template slot-scope="scope">
                  {{ scope.row.quantity * scope.row.prices[currentSelectedPointType - 1].unitPrice }}
                </template>
              </el-table-column>
              <el-table-column
                :label="$t('OrderFoodItemManagement.Action')"
                width="100"
              >
                <template slot-scope="scope">
                  <el-button
                    plain
                    size="mini"
                    @click="removeItem(scope.$index)"
                  >
                    {{ $t('General.Remove') }}
                  </el-button>
                </template>
              </el-table-column>
            </el-table>
          </div>
          <!-- 點數不足提示 -->
          <div v-show="isPointsInsufficient" class="points-insufficient-message">
            <span>{{ $t('OrderFood.PointsInsufficientMessage') }}</span>
          </div>
          <!-- 總計使用點數 -->
          <div class="total-button">
            <div class="value">
              {{ total }}
            </div>
            <div class="tips">
              {{ $t('OrderFood.TotalUsedPoints') }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- 選擇規格對話框 -->
    <SelectSpecDialog
      ref="selectSpecDialogRef"
      mode="order"
      :target-index="currentSpecTargetFoodItem.index"
      :target-id="currentSpecTargetFoodItem.id"
      :selected-spec="currentSpecTargetFoodItem.chooses"
      @confirm="handleSelectedSpec"
    />
  </div>
</template>

<script>
import { mapState } from 'vuex'
import SelectSpecDialog from './SelectSpecDialog.vue'
import {
  getDisplaySpec,
  getRememberedPointType,
  setRememberedPointType,
  resolveDefaultPointType
} from '@/js/utils/orderFood'
import { swiper, swiperSlide } from 'vue-awesome-swiper'
import 'swiper/dist/css/swiper.css'
export default {
  name: 'SelectFoodItems',
  components: {
    SelectSpecDialog,
    swiper,
    swiperSlide
  },
  data () {
    return {
      isLoading: true, // 是否載入中
      isLoadingHouseholdInfo: false, // 是否載入中戶別資訊
      currentSelectedPointType: 1, // 選擇的點數類型
      selectedDiningMode: 2, // 用餐方式 1: 內用 2: 現場外帶 3: 預約 (僅智生活App)
      selectedTableNo: '', // 內用桌號
      menuSwiperList: [], // 餐點類別列表
      // Swiper 選項
      menuSwiperOptions: {
        freeMode: true,
        slidesPerView: 5,
        pagination: true,
        allowTouchMove: false,
        spaceBetween: 12
      },
      // Swiper 導航按鈕狀態
      menuSwiperNavBtnStatus: {
        isBegin: true,
        isNextEnable: false,
        isEnd: false
      },
      currentSelectedMenu: null, // 當前選擇的餐點類別
      currentMenuItemList: [], // 當前選擇的餐點類別的品項列表
      selectedFoodItemList: [], // 已選擇的餐點品項
      currentClickedFoodItemId: null, // 當前點擊的餐點品項
      isSwitchingPointTypeBack: false, // 是否正在將點數類型切換回去
      isAutoSelectingPointType: false, // 是否正在由系統自動導向點數類型（不寫入記憶值）
      selectSpecDialogRef: null, // 選擇規格對話框
      // 當前規格編輯的餐點品項
      currentSpecTargetFoodItem: {
        index: -1,
        id: '',
        chooses: []
      }
    }
  },
  computed: {
    ...mapState({
      menuList: state => state.orderFood.menuList,
      specList: state => state.orderFood.specList,
      pointTypeList: state => state.orderFood.pointTypeList,
      currentSelectedHouseholdInfo: state => state.orderFood.currentTransaction.selectedHouseholdInfo.current,
      isPointsInsufficient: state => state.orderFood.currentTransaction.isPointsInsufficient, // 是否點數不足
      isPointSupplementEnabled: state => state.orderFood.currentTransaction.isPointSupplementEnabled // 是否啟用點數補足
    }),
    clubhouseTables () {
      return this.$store.getters.getClubhouseTables || []
    },
    // 過濾後的餐點品項列表
    filteredFoodItemList () {
      const targetList = []
      // 判斷點數類型與餐點類別是否皆選擇
      if (this.currentSelectedPointType && this.currentSelectedMenu) {
        const targetMenu = this.menuList.find(item => item.id === this.currentSelectedMenu.id)
        if (targetMenu) {
          /**
           * 篩選條件:
           * 1. 餐點品項本身有啟用
           * 2. 餐點品項中對應的點數類型有啟用
           */
          const activeFoodItemList = targetMenu.children.filter((item) => item.isCheck && item.prices[this.currentSelectedPointType - 1].isCheck)
          activeFoodItemList.forEach((item) => {
            targetList.push({
              ..._cloneDeep(item),
              menuId: targetMenu.id, // 餐點類別ID
              menuType: targetMenu.name, // 餐點類別名稱
              isClicked: false
            })
          })
        }
      }
      return targetList
    },
    // 總計使用點數
    total () {
      return this.selectedFoodItemList.reduce(
        (accumulator, currentValue) => accumulator + currentValue.quantity * currentValue.prices[this.currentSelectedPointType - 1].unitPrice,
        0
      )
    },
    // 餐點品項 Swiper 實例
    menuSwiperRef () {
      return this.$refs.menuSwiper.swiper
    }
  },
  watch: {
    // 觀察已選擇的餐點品項
    selectedFoodItemList: {
      handler: function (newVal) {
        this.$emit('selectFoodItems', newVal)
      },
      deep: true,
      immediate: true
    },
    // 觀察點數類型
    currentSelectedPointType (newVal, oldVal) {
      if (this.isSwitchingPointTypeBack) {
        this.isSwitchingPointTypeBack = false
        return
      }
      // 系統自動導向的變更不算手動切換，不寫入記憶值
      if (this.isAutoSelectingPointType) {
        this.isAutoSelectingPointType = false
        this.$emit('selectPointType', newVal)
        return
      }
      // 判斷是否已選擇餐點品項
      if (this.selectedFoodItemList.length) {
        this.$confirm(this.$t('OrderFood.SwitchPointTypeAlert'), this.$t('OrderFood.IfClearSelectedFoodItems'), {
          confirmButtonText: this.$t('OrderFood.Switch'),
          cancelButtonText: this.$t('General.Cancel'),
          roundButton: true,
          type: 'warning',
          closeOnClickModal: false
        }).then(() => {
          this.selectedFoodItemList = [] // 清空已選擇的品項
          this.currentClickedFoodItemId = null // 清空當前點擊的餐點品項
          setRememberedPointType(newVal)
          this.$emit('selectPointType', newVal)
        }).catch(() => {
          this.currentSelectedPointType = oldVal
          this.isSwitchingPointTypeBack = true
        })
      } else {
        setRememberedPointType(newVal)
        this.$emit('selectPointType', newVal)
      }
    },
    // 觀察用餐方式
    selectedDiningMode (newVal) {
      // 如果不是內用，則清空桌號
      if (newVal !== 1) {
        this.selectedTableNo = ''
      }
      this.$emit('selectDiningMode', { diningMode: newVal, tableNo: this.selectedTableNo })
    },
    // 觀察內用桌號
    selectedTableNo (newVal) {
      // 如果是內用，則發送事件
      if (this.selectedDiningMode === 1) {
        this.$emit('selectDiningMode', { diningMode: this.selectedDiningMode, tableNo: newVal })
      }
    },
    // 觀察總計使用點數
    total () {
      this.handleCalculatePointsInsufficient()
    },
    // 觀察當前選擇的戶別狀態(主要是點數)
    currentSelectedHouseholdInfo: {
      handler () {
        this.handleCalculatePointsInsufficient()
      },
      deep: true
    }
  },
  mounted () {},
  /**
   * OrderFood 是 keepAlive，所以選擇在這裡處理相關邏輯
   */
  async activated () {
    try {
      await Promise.all([
        this.$store.dispatch('getPointType'),
        this.$store.dispatch('getFoodMenu'),
        this.$store.dispatch('getFoodSpec')
      ])
      this.autoSelectPointType()
    } catch (error) {
      this.$message.error(error?.message || this.$t('General.ServerError'))
    } finally {
      this.isLoading = false
    }
    this.initMenuSwiperList()
    if (this.menuSwiperList.length > 0) {
      const targetMenu = this.menuSwiperList.find(item => item.id === this.currentSelectedMenu?.id) || this.menuSwiperList[0]
      this.clickMenu(targetMenu)
    }
    this.$emit('selectPointType', this.currentSelectedPointType)
  },
  methods: {
    /**
     * 點數類型自動導向
     * 已選品項時不套用，避免打斷進行中的點餐
     */
    autoSelectPointType () {
      if (this.selectedFoodItemList.length) return
      const targetPointType = resolveDefaultPointType({
        menuList: this.menuList,
        pointTypeList: this.pointTypeList,
        rememberedPointType: getRememberedPointType()
      })
      if (targetPointType === this.currentSelectedPointType) return
      this.isAutoSelectingPointType = true
      this.currentSelectedPointType = targetPointType
    },
    // 初始化餐點類別列表
    initMenuSwiperList () {
      this.menuSwiperList = []
      this.menuList.filter(item => item.isCheck === 1).forEach((item) => {
        this.menuSwiperList.push({
          id: item.id,
          name: item.name,
          isSelected: false
        })
      })
      this.$nextTick(() => {
        this.menuSwiperNavBtnStatus.isNextEnable = this.menuSwiperList.length > this.menuSwiperOptions.slidesPerView
      })
    },
    // 更新 Swiper 導航按鈕狀態
    updateMenuSwiperNavBtnStatus () {
      this.menuSwiperNavBtnStatus.isBegin = this.menuSwiperRef.isBeginning
      this.menuSwiperNavBtnStatus.isEnd = this.menuSwiperRef.isEnd
    },
    /**
     * 處理 Swiper 滑動
     * @param {'prev' | 'next'} direction 滑動方向
     */
    handleSwiperSlide (direction) {
      if (direction === 'prev') {
        this.menuSwiperRef.slidePrev()
      } else {
        this.menuSwiperRef.slideNext()
      }
      this.updateMenuSwiperNavBtnStatus()
    },
    /**
     * 點擊餐點類別
     * @param {*} menu
     */
    clickMenu (menu) {
      this.currentSelectedMenu = menu
      this.menuSwiperList.forEach((item) => {
        item.isSelected = item.id === menu.id
      })
    },
    /**
     * 點擊餐點品項
     * @param {*} foodItem
     */
    clickItem (foodItem) {
      this.currentClickedFoodItemId = foodItem.id // 紀錄當前點擊的餐點品項

      const cloneFoodItem = _cloneDeep(foodItem) // 複製餐點品項
      const newFoodItem = {
        ...cloneFoodItem,
        quantity: 1,
        displaySpec: '',
        hasSpec: cloneFoodItem.chooses.flatMap(spec => spec.children).some(opt => opt.isOption === 1)
      }
      if (newFoodItem.hasSpec) {
        newFoodItem.chooses = newFoodItem.chooses.map(item => {
          return {
            ...item,
            children: item.children.filter(opt => opt.isOption === 1)
          }
        })
      }
      this.selectedFoodItemList.push(newFoodItem)

      const tableBody = document.getElementsByClassName('el-table__body-wrapper')[0]
      this.$nextTick(() => {
        // 到新增項目的高度（最下方）
        const scrollHeight = tableBody.scrollHeight
        tableBody.scrollTo(0, scrollHeight)
      })
    },
    /**
     * 移除項目
     * @param {number} index 目標索引
     */
    removeItem (index) {
      this.selectedFoodItemList.splice(index, 1)
    },
    // 取得資料列樣式
    getRowClassName ({ row }) {
      if (row.id === this.currentClickedFoodItemId) {
        return 'active'
      }
    },
    /**
     * 處理打開選擇規格對話框
     * @param {number} index 目標索引
     */
    handleOpenSelectSpecDialog (index) {
      this.currentSpecTargetFoodItem.index = index
      this.currentSpecTargetFoodItem.id = this.selectedFoodItemList[index].id
      this.currentSpecTargetFoodItem.chooses = this.selectedFoodItemList[index].chooses
      this.$refs.selectSpecDialogRef.open()
    },
    /**
     * 處理選擇規格
     * @param {*}
     */
    handleSelectedSpec ({ index, selectedSpec }) {
      this.selectedFoodItemList[index].chooses = [...selectedSpec]
      this.selectedFoodItemList[index].displaySpec = getDisplaySpec(selectedSpec, ', ', (opt) => opt.isChecked)
    },
    // 處理更新戶別點數
    async handleUpdateHouseholdPoints () {
      try {
        // 防止連點：正在載入中就直接忽略
        if (this.isLoadingHouseholdInfo) return
        this.isLoadingHouseholdInfo = true
        await this.$store.dispatch('updateHouseholdPoints')
      } catch (error) {
        this.$message.error(error?.message || this.$t('General.ServerError'))
      } finally {
        setTimeout(() => {
          this.isLoadingHouseholdInfo = false
        }, 2000)
      }
    },
    // 計算點數是否不足
    async handleCalculatePointsInsufficient () {
      if (this.currentSelectedHouseholdInfo.tabletId) {
        // 未啟用點數補足: 只依照當前交易類型的點數來計算
        if (!this.isPointSupplementEnabled) {
          const currentPointValue = this.currentSelectedPointType === 1
            ? this.currentSelectedHouseholdInfo.normalPoint
            : this.currentSelectedHouseholdInfo.cashPoint
          // 紀錄目前交易的點數不足狀態
          this.$store.commit('setCurrentTransactionPointsInsufficient', currentPointValue - this.total < 0)
        } else {
          // 啟用點數補足: 將一般與現金兩種點數加總來計算
          const totalPoints = this.currentSelectedHouseholdInfo.normalPoint + this.currentSelectedHouseholdInfo.cashPoint
          // 紀錄目前交易的點數不足狀態
          this.$store.commit('setCurrentTransactionPointsInsufficient', totalPoints - this.total < 0)
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.select-food-items {
  display: flex;
  flex-direction: column;
  gap: 20px;
  .main-header {
    width: 100%;
    display: flex;
    flex-wrap: wrap;

    &__left {
      flex: 1;
      flex-direction: column;
    }
    &__point-type {
      display: flex;
      padding: 10px 0;
      .title {
        padding-right: 20px;
      }
    }
    &__dining-mode {
      display: flex;
      padding: 10px 0;
    }

    &__right {
      flex: 1;
    }
    &__household-info {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      font-size: map-get($fontSize, small);
      padding: 10px 0;
      & span:not(:last-child) {
        margin-right: map-get($fontSize, x-large);
      }
    }
    &__update-household-points {
      display: flex;
      align-items: center;
      gap: 4px;
      cursor: pointer;
      color: $mainColor;
      user-select: none;
      &-icon {
        margin-right: 2px !important;
      }
      &-text {
        text-decoration: underline;
      }
    }
  }
  .dining-mode-select {
    width: 100%;
    display: flex;
    align-items: center;

    &__title {
      color: $mainFontColor;
      padding-right: 20px;
    }

    &__options {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
    }

    &__table-select {
      margin-left: 12px;
      width: 150px;
    }
  }
  .main-container {
    height: 60vh;
    padding: 20px;
    background-color: $bgDark4;
    display: flex;
    width: 100%;
    box-sizing: border-box;
    text-align: left;
    gap: 20px;

    .container-title {
      font-size: map-get($fontSize, large);
      color: $subFontColor;
      font-weight: bold;
      height: 20px;
      display: flex;
      align-items: center;
      flex-shrink: 0;
    }
    .item-list-container {
      flex-basis: 35%;
      width: 35%;
      height: 100%;
      display: flex;
      flex-direction: column;
      gap: 10px;
      .item-types-wrapper {
        position: relative;
        width: 100%;
        height: 70px;
        background-color: #fff;
        border: 1px $borderDark2 solid;
        box-sizing: border-box;
        padding: 0 20px;
        &__navigation-btn {
          position: absolute;
          z-index: 10;
          top: 0;
          width: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          height: 100%;
          cursor: pointer;
          color: $mainColor;
          &.left {
            left: 0;
          }
          &.right {
            right: 0;
          }
        }
      }
      .item-types-swiper {
        width: 100%;
        height: 100%;
      }
      .item-types-slide {
        height: 100%;
        background-color: #fff;
        display: flex;
        justify-content: center;
        align-items: center;

        &__content {
          position: relative;
          width: 100%;
          height: 100%;
          display: flex;
          justify-content: center;
          align-items: center;
          cursor: pointer;
          font-weight: bold;
          user-select: none;
          text-align: center;
          &.isSelected {
            color: $mainColor;
          }
          &.isSelected::after {
            content: '';
            position: absolute;
            bottom: 0;
            left: 0;
            width: 100%;
            height: 3px;
            background-color: $mainColor;
          }
        }
      }
      .item-list-wrapper {
        overflow-y: auto;
        height: 100%;
        background-color: #fff;
        border: 1px $borderDark2 solid;
        display: flex;
        flex-direction: column;
        padding: 8px;
        .category-and-items-wrapper {
          display: flex;
          flex-direction: column;
          gap: 20px;
          .category {
            font-size: map-get($fontSize, xx-large);
            font-weight: bold;
            padding-left: 20px;
          }
          .items-by-category {
            display: flex;
            flex-direction: column;
            .item {
              cursor: pointer;
              padding: 7px 0px 7px 40px;
              font-size: map-get($fontSize, xx-large);
              display: flex;
              flex-direction: column;
              &.active {
                background-color: $defaultColorHover;
              }
              &:not(.active):hover {
                background-color: $bgDark3;
              }
            }
          }
        }
      }
    }
    .selected-item-list-container {
      flex-basis: 65%;
      width: 65%;
      height: 100%;
      display: flex;
      flex-direction: column;
      gap: 10px;
      overflow-y: hidden;
      .selected-item-list-wrapper {
        width: 100%;
        height: calc(100% - 20px);
        box-sizing: border-box;
        background-color: #fff;
        border: 1px $borderDark2 solid;
        display: flex;
        flex-direction: column;
        .el-table-container {
          height: calc(100% - 110px);
          display: flex;
          align-items: center;
          &.is-point-insufficient {
            height: calc(100% - 150px);
          }
          ::v-deep .el-table {
            height: 100%;
            box-sizing: border-box;
            .active {
              background-color: $defaultColorHover;
            }
            .el-input-number {
              width: 100%;
            }
            tr th {
              background-color: $bgDark3;
            }
            .cell,
            .el-table__empty-text {
              text-align: center;
              color: $subFontColor;
            }
          }
        }
        .points-insufficient-message {
          width: 100%;
          height: 40px;
          color: $dangerColor;
          display: flex;
          justify-content: center;
          align-items: center;
          font-size: map-get($fontSize, medium);
          flex-shrink: 0;
        }
        .total-button {
          background-color: $bgOrange;
          color: #fff;
          display: flex;
          flex: 1;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          .value {
            font-size: 60px;
          }
          .tips {
            font-size: 17px;
          }
        }
      }
    }
  }
  .point-container {
    &__title {
      margin-right: 4px;
    }
    &__icon {
      color: $mainColor;
    }
  }
  &__menu-item-list {
    display: flex;
    flex-direction: column;
  }
  &__menu-item {
    display: flex;
    align-items: center;
    font-size: map-get($fontSize, large);
    padding: 8px 4px;
    cursor: pointer;
    &:not(:last-child) {
      margin-bottom: 12px;
    }
    &.isClicked {
      background-color: $defaultColorHover;
    }
  }
}
</style>
